import { Icons } from '@/libs/components/src/lib/icons';
import { BillableTransactionTypes } from '@/libs/core/src/lib/types/PatientTransaction.type';
import { Button } from '@chiroup/components/Button';
import { DatabaseTable } from '@chiroup/components/DatabaseTable';
import { NoRowsFound } from '@chiroup/components/NoRowsFound';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Select } from '@chiroup/components/form/Select';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import useDevtools from '../../../../hooks/useDevtools';

type UpdateBillingProfileFormData = {
  onlyNulls: boolean;
  sourceBillingProfileId?: string;
  targetBillingProfileId?: string;
};

type BillingProfileUsageResult = {
  billingProfileId: number | null;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  active: number;
  transaction_count: number;
  transactions_with_plis: number;
  transactions_without_plis: number;
  integrationId: number | null;
  integrationName: string | null;
};

type BillingProfileOption = {
  value: string;
  text: string;
  active?: number;
};

type Props = {
  clinicId: number;
};

const BillingProfilePanel: React.FC<Props> = ({ clinicId }) => {
  const { getBillingProfileUsage } = useDevtools('billing-panel') as any;
  const { getActiveBillingProfiles, updateBillingProfileIds, previewBillingProfileUpdate } =
    useDevtools('billing-panel') as any;

  // Update form
  const {
    handleSubmit: handleUpdateSubmit,
    control: updateControl,
    formState: { errors: updateErrors },
    watch,
  } = useForm<UpdateBillingProfileFormData>({
    defaultValues: {
      onlyNulls: false,
      sourceBillingProfileId: undefined,
      targetBillingProfileId: undefined,
    },
  });

  // State
  const [usageResults, setUsageResults] = useState<BillingProfileUsageResult[]>([]);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [allBillingProfiles, setAllBillingProfiles] = useState<BillingProfileOption[]>([]);
  const [activeBillingProfiles, setActiveBillingProfiles] = useState<BillingProfileOption[]>([]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [updateResult, setUpdateResult] = useState<{ affectedRows: number } | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const onlyNulls = watch('onlyNulls');

  // Load billing profiles on mount
  useEffect(() => {
    loadBillingProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId]);

  const loadBillingProfiles = async () => {
    setIsLoadingProfiles(true);

    try {
      const response = await getActiveBillingProfiles({ clinicId });

      if (response.all) {
        const sorted = [...response.all].sort((a: any, b: any) =>
          (a.sortKey || '').localeCompare(b.sortKey || '')
        );
        setAllBillingProfiles(sorted);
      }

      if (response.active) {
        const sorted = [...response.active].sort((a: any, b: any) =>
          (a.sortKey || '').localeCompare(b.sortKey || '')
        );
        setActiveBillingProfiles(sorted);
      }
    } catch (error: any) {
      console.error('Failed to load billing profiles:', error);
      alert(`Failed to load billing profiles: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  const onUsageFetch = async () => {
    setIsLoadingUsage(true);
    setUsageResults([]);

    try {
      const response = await getBillingProfileUsage({ clinicId });

      if (response.results) {
        setUsageResults(response.results);
      }
    } catch (error: any) {
      console.error('Failed to get billing profile usage:', error);
      alert(`Failed to get billing profile usage: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const onPreview = async () => {
    const data = watch();

    if (!data.onlyNulls && !data.sourceBillingProfileId) {
      alert(
        'Please either enable "Only update nulls" or choose a Source Billing Profile ID filter before previewing.'
      );

      return;
    }

    setIsLoadingPreview(true);
    setPreviewCount(null);
    setUpdateResult(null);
    setUpdateError(null);

    try {
      const response = await previewBillingProfileUpdate({
        clinicId,
        onlyNulls: data.onlyNulls,
        sourceBillingProfileId: data.sourceBillingProfileId
          ? parseInt(data.sourceBillingProfileId, 10)
          : null,
      });

      setPreviewCount(response.count);
    } catch (error: any) {
      console.error('Failed to preview update:', error);
      alert(`Failed to preview update: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const onUpdateSubmit = async (data: UpdateBillingProfileFormData) => {
    const targetId = data.targetBillingProfileId;
    const sourceId = data.sourceBillingProfileId;

    if (!data.onlyNulls && !data.sourceBillingProfileId) {
      alert(
        'Please either enable "Only update nulls" or choose a Source Billing Profile ID filter before updating.'
      );

      return;
    }

    setIsUpdating(true);
    setUpdateResult(null);
    setUpdateError(null);

    try {
      const response = await updateBillingProfileIds({
        clinicId,
        onlyNulls: data.onlyNulls,
        sourceBillingProfileId: sourceId ? parseInt(sourceId, 10) : null,
        targetBillingProfileId:
          targetId === 'NULL' ? null : targetId ? parseInt(targetId, 10) : null,
      });

      setUpdateResult(response);
      setPreviewCount(null);
    } catch (error: any) {
      console.error('Failed to update billing profile IDs:', error);
      setUpdateError(error?.response?.data?.message || error.message || 'An error occurred.');
      setPreviewCount(null);
    } finally {
      setIsUpdating(false);
    }
  };

  const usageTableSchema = useMemo(
    () => ({
      billingProfileId: {
        label: 'ID',
        showOnList: true,
        td: (row: BillingProfileUsageResult) =>
          row.billingProfileId !== null ? row.billingProfileId : '(NULL)',
      },
      name: {
        label: 'Name',
        showOnList: true,
        td: (row: BillingProfileUsageResult) => {
          if (row.name) {
            return row.name;
          } else if (row.firstName && row.lastName) {
            return `${row.lastName}, ${row.firstName}`;
          }

          return '(No name)';
        },
      },
      active: {
        label: 'Active',
        showOnList: true,
        td: (row: BillingProfileUsageResult) => {
          if (row.billingProfileId === null) {
            return null;
          }

          return (
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                Number(row.active) === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {Number(row.active) === 1 ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
      transaction_count: {
        label: 'Transactions',
        showOnList: true,
        tdClassName: 'text-right',
        thClassName: 'text-right',
        td: (row: BillingProfileUsageResult) => row.transaction_count.toLocaleString(),
      },
      tx_combined: {
        label: 'Claims/Eligible',
        thClassName: 'text-right',
        td: (row: BillingProfileUsageResult) => (
          <div>
            <span>{row.transactions_with_plis.toLocaleString()}</span>
            <span className="px-2">/</span>
            <span className="text-green-600 font-bold">
              {row.transactions_without_plis.toLocaleString()}
            </span>
          </div>
        ),
      },

      integration_combined: {
        label: 'Integration',
        td: (row: BillingProfileUsageResult) => {
          return row.integrationId ? (
            <div>
              <span>{row.integrationId !== null ? row.integrationId : '(NULL)'}</span>
              <span className="px-2"> - </span>
              <span>{row.integrationName !== null ? row.integrationName : '(NULL)'}</span>
            </div>
          ) : null;
        },
      },
    }),
    []
  );

  return (
    <div className="space-y-8">
      {/* Clinic ID Display */}
      <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
        <p className="text-sm">
          <strong>Clinic ID:</strong> {clinicId}
        </p>
      </div>

      {/* Billing Profile Usage Report Section */}
      <section className="bg-white border border-gray-400 inset-4 rounded-lg">
        <div className="p-4 flex flex-row justify-between">
          <h1 className="font-extrabold">Billing Profile Usage Report</h1>
          <Button
            text="Fetch"
            disabled={isLoadingUsage}
            loading={isLoadingUsage}
            onClick={onUsageFetch}
            type="button"
            icon={Icons.puzzle}
          />
        </div>
        <div className="p-4 m-4 border border-gray-400 bg-gray-50 rounded-lg text-sm space-y-2">
          <p>
            This report shows which billing profiles are being used by patient transactions in this
            clinic.
          </p>
          <p>
            <strong>Note:</strong> Transactions that have been submitted to the payor for processing{' '}
            <strong>are not eligible</strong> for update. The number (claims) is provided purely for
            reference. Transactions without payor line items can be updated with the bulk-update
            tool below.{' '}
          </p>
          <p>
            <strong>Billable transaction types:</strong> {BillableTransactionTypes.join(', ')}.{' '}
          </p>
          <p className="text-red-600">
            <strong>Warning:</strong> Inactive billing profiles will cause errors when attempting to
            submit claims. Transactions using inactive profiles should be reassigned.
          </p>
        </div>

        {/* Usage Results Table */}
        <DatabaseTable
          instanceTitle="Billing Profile Usage"
          schema={usageTableSchema}
          rows={usageResults.length > 0 ? usageResults : null}
          count={usageResults.length}
          isFetching={isLoadingUsage}
          clickable={false}
          options={{
            sectionClassName:
              'border border-gray-300 dark:border-dark-gray-600 rounded-md overflow-hidden',
            defaultTdClassName: 'px-6 py-2 align-middle',
            defaultThClassName:
              'px-6 py-2 text-left text-sm text-gray-900 dark:text-dark-gray-50 tracking-wide whitespace-nowrap',
            noRowsFoundComponent: (
              <NoRowsFound name="Billing Profile Usage">
                Click Fetch to see billing profile usage.
              </NoRowsFound>
            ),
          }}
          parentClassName="p-4"
        />
      </section>

      {/* Divider */}
      <div className="border-t-2 border-gray-300 my-8"></div>

      {/* Update Billing Profile IDs Section */}
      <form onSubmit={handleUpdateSubmit(onUpdateSubmit)}>
        <section className="bg-white border border-gray-400 inset-4 rounded-lg">
          <div className="p-4 flex flex-row justify-between">
            <h1 className="font-extrabold">Bulk Update Billing Profile IDs</h1>
            <div className="flex gap-2">
              <Button
                text="Preview"
                onClick={onPreview}
                type="button"
                disabled={isLoadingPreview || isUpdating}
                loading={isLoadingPreview}
                icon={Icons.clock}
              />
              <Button
                text="Update"
                disabled={isUpdating || isLoadingPreview || previewCount === null}
                loading={isUpdating}
                type="submit"
                icon={Icons.puzzle}
              />
            </div>
          </div>

          {/* Preview Results */}
          {previewCount !== null && (
            <div className="p-4 m-4 border border-blue-400 bg-blue-50 rounded-lg text-sm">
              <div className="flex items-start space-x-2">
                <span className="text-blue-600" role="img" aria-label="chart">
                  📊
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-blue-800">
                    Preview: {previewCount.toLocaleString()} transactions will be affected
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Update Results */}
          {updateResult && (
            <div className="p-4 m-4 border border-green-400 bg-green-50 rounded-lg text-sm">
              <div className="flex items-start space-x-2">
                <span className="text-green-600" role="img" aria-label="checkmark">
                  ✅
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-green-800">
                    Success! Updated {updateResult.affectedRows.toLocaleString()} transactions
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Results */}
          {updateError && (
            <div className="p-4 m-4 border border-red-400 bg-red-50 rounded-lg text-sm">
              <div className="flex items-start space-x-2">
                <span className="text-red-600" role="img" aria-label="error">
                  ❌
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-red-800">Error: {updateError}</p>
                </div>
              </div>
            </div>
          )}

          <div className="p-4 m-4 border border-red-400 bg-red-50 rounded-lg text-sm space-y-4">
            <p className="text-red-500 flex justify-center font-bold">
              * * * DANGER - BULK UPDATE OPERATION * * *
            </p>
            <p>
              <strong>Billable transaction types:</strong> {BillableTransactionTypes.join(', ')}
              .{' '}
            </p>
            <p>
              This operation will bulk update the billing profile on patient transactions
              (purchases). Use with extreme caution.
            </p>
            <p>
              <strong>Note:</strong> Transactions that have been submitted to the payor for
              processing <strong>are not eligible</strong> for update and will not be included in
              the number of transactions that will be affected.
            </p>
            <p>
              <strong>Preview first</strong> to see how many records will be affected before
              executing the update.
            </p>
          </div>
          <div className="p-4 flex flex-col space-y-4">
            <Controller
              name="onlyNulls"
              control={updateControl}
              render={({ field }) => (
                <Checkbox
                  value={field?.value}
                  onChange={field.onChange}
                  label="Only update nulls"
                  tooltip="If checked, only update transactions where billingProfileId is NULL"
                  className="col-span-2"
                />
              )}
            />

            <Controller
              name="sourceBillingProfileId"
              control={updateControl}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Source Billing Profile ID (filter)"
                  options={[
                    { value: '', text: '-- All (no filter) --' },
                    ...allBillingProfiles.map(bp => ({
                      value: bp.value,
                      text: Number(bp.active) === 0 ? `${bp.text} (inactive)` : bp.text,
                    })),
                  ]}
                  disabled={isUpdating || isLoadingProfiles || onlyNulls}
                  tooltip="Optional: Only update transactions with this specific billing profile ID. Leave as 'All' to update all transactions (filtered by 'Only update nulls' checkbox)."
                  errors={
                    updateErrors.sourceBillingProfileId
                      ? ({
                          message: updateErrors.sourceBillingProfileId.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />

            <Controller
              name="targetBillingProfileId"
              control={updateControl}
              rules={{ required: 'Required.' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label="Target Billing Profile ID"
                  options={[
                    { value: 'NULL', text: '-- Set to NULL --' },
                    ...activeBillingProfiles.map(bp => ({
                      value: bp.value,
                      text: bp.text,
                    })),
                  ]}
                  disabled={isUpdating || isLoadingProfiles}
                  tooltip="The billing profile ID to set on the matching transactions. Select 'Set to NULL' to clear the billing profile."
                  errors={
                    updateErrors.targetBillingProfileId
                      ? ({
                          message: updateErrors.targetBillingProfileId.message,
                        } as FormError)
                      : undefined
                  }
                />
              )}
            />
          </div>
        </section>
      </form>
    </div>
  );
};

export default BillingProfilePanel;
